import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { FullScreenLoading, NUGGET_VARIANTS } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { usePageTitle } from '@backrs_ui/grow-ui/src/hooks/usePageTitle';
import { getBookmarkIdForNugget } from '@backrs_ui/grow-ui/src/pages/Bookmarks/pages/bookmarks.util';
import {
  usePostBookmarkMutation,
  useDeleteBookmarkMutation,
  useGetBookmarksQuery,
} from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { useGetInternshipByIdQuery } from '@backrs_ui/grow-ui/src/redux/internship/internship.api';
import { PagePrompt } from '@backrs_ui/grow-ui/src/redux/pagePrompts/pagePrompts';
import { useRegisterPagePrompts } from '@backrs_ui/grow-ui/src/redux/pagePrompts/useRegisterPagePrompts';

import { InternshipPage } from './InternshipPage';
import { useAgentContext } from './InternshipPageContainer.agentContext';

const INTERNSHIP_PROMPTS: readonly PagePrompt[] = [
  { promptButtonId: 'internship_am_i_a_good_fit', title: 'Am I a strong candidate?' },
  { promptButtonId: 'internship_increase_my_odds', title: 'Increase my odds' },
  { promptButtonId: 'internship_whats_the_upside', title: 'What are the advantages in this role?' },
];

export const InternshipPageContainer = () => {
  const { internshipId } = useParams();

  const navigate = useNavigate();
  const [postBookmark] = usePostBookmarkMutation();
  const [deleteBookmark] = useDeleteBookmarkMutation();
  const { data: bookmarks } = useGetBookmarksQuery(undefined);

  const {
    data: foundInternship,
    isLoading,
    error,
  } = useGetInternshipByIdQuery(internshipId || '', {
    skip: !internshipId,
  });

  useRegisterPagePrompts(foundInternship ? INTERNSHIP_PROMPTS : null);

  const internshipBookmarkId = getBookmarkIdForNugget(
    bookmarks,
    foundInternship?.id,
    NUGGET_VARIANTS.INTERNSHIP,
  );

  useEffect(() => {
    if (!internshipId || error) {
      if (error) {
        console.error('Failed to load internship', internshipId, error);
      }

      navigate(APP_ROUTE.NOT_FOUND_404);
    }
  }, [navigate, internshipId, error]);

  usePageTitle(foundInternship?.role_title);
  useAgentContext(foundInternship);

  if (!internshipId || isLoading || !foundInternship || error) {
    return <FullScreenLoading />;
  }

  const handleBookmark = async () => {
    const result = internshipBookmarkId
      ? await deleteBookmark(internshipBookmarkId)
      : await postBookmark({ type: 'internship', itemId: foundInternship.id });

    if ('error' in result) {
      console.error('Bookmark mutation failed', result.error);
    }
  };

  return (
    <InternshipPage
      internship={foundInternship}
      handleBookmark={handleBookmark}
      hasBookmarked={!!internshipBookmarkId}
    />
  );
};
